import '@vizia/vizia-css';

import React, {Suspense} from 'react';
import ReactDOM from 'react-dom';
import {Provider} from 'react-redux';
import jwtDecode from 'jwt-decode';

import {startHistory} from './history';
import rollbar from './utils/rollbar';
import store from './redux/store';
import auth from './auth';
import {getOtp} from './selectors';
import events from './lib/events';
import * as workerManager from './lib/workerManager';
import initFlags from './utils/featureFlags';
import {isPublic, isPrivateLink} from './utils/url';
import {DeviceTypeProvider} from './DeviceType.jsx';
import {ProgressInfinite} from '@brandwatch/axiom-components';

import {deckReceive} from './actions/decks';

const noop = () => {};

startHistory();

window.Rollbar = rollbar();
workerManager.register();

if (window.screen && window.screen.orientation && typeof window.screen.orientation.lock === 'function') {
  window.screen.orientation.lock('portrait').catch(noop);
}

const rootEl = document.getElementById('main');

const otp = getOtp(store.getState());

// Creates and returns a donny TokenStore that's initialised with the OTP.
// Needless to say public URLs won't require auth by virtue of being public.
// Auth for private links is handled in components relating to that, hence it's skipped here.
if (!auth.hasStore(otp) && !isPublic() && !isPrivateLink()) {
  auth.init(otp);
}

events.on('deck:update', deck => deckReceive(deck)(store.dispatch, store.getState));
events.on('folder:update', payload => store.dispatch({type: 'FOLDER_RECEIVE', payload}));

events.on('slide:update', payload => store.dispatch({type: 'SLIDE_RECEIVE', payload}));
events.on('slide:insert', payload => store.dispatch({type: 'SLIDE_RECEIVE', payload}));

events.on('component:update', payload => store.dispatch({type: 'COMPONENT_RECEIVE', payload}));
events.on('component:insert', payload => store.dispatch({type: 'COMPONENT_RECEIVE', payload}));
events.on('component:read', payload => store.dispatch({type: 'COMPONENT_RECEIVE', payload}));

if (auth.hasStore(otp)) {
  auth.isAuthenticated().then(token => {
    if (!token) {
      console.warn('Not logged in...'); // eslint-disable-line no-console
      return;
    }

    initFlags(window.env.BOOTSTRAP2_LAUNCH_DARKLY_CLIENT_KEY, {
      kind: 'user',
      key: String(jwtDecode(token).clientId)
    });
  }).catch(err => {
    setTimeout(() => {
      const state = store.getState();
      if (state && !state.alerts.dialog.isOpen) {
        store.dispatch({
          type: 'ALERT_DIALOG_OPEN',
          payload: {
            type: 'error',
            header: 'Authorization Error',
            body: err && err.message || 'Unknown Error',
            isOpen: true,
            template: 'invite_unknown_error'
          }
        });
      }
    }, 2000);
  });
}

const AlertBar = React.lazy(() => import(/* webpackChunkName: 'alert-bar' */ './containers/AlertBar.js'));
const AlertDialog = React.lazy(() => import(/* webpackChunkName: 'alert-dialog' */ './containers/AlertDialog.js'));
const Router = React.lazy(() => import(/* webpackChunkName: 'router' */ './Router.jsx'));

const fallbackStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const fallback = (
  <div style={fallbackStyle}>
    <ProgressInfinite />
  </div>
);

ReactDOM.render(
  <Provider store={store}>
    <DeviceTypeProvider>
      <Suspense fallback={fallback}>
        <AlertBar/>
        <Router />
        <AlertDialog />
      </Suspense>
    </DeviceTypeProvider>
  </Provider>,
  rootEl
);
